import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';
import {
    User,
    Bell,
    Video,
    Shield,
    CreditCard,
    Moon,
    Sun,
    Save,
    Upload
} from 'lucide-react';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@/components/ui/tabs';

export const Settings = () => {
    const { theme, toggleTheme, isDark } = useTheme();
    const [profile, setProfile] = useState({
        name: 'John Doe',
        email: 'john@example.com',
        company: 'Acme Inc.'
    });
    const [notifications, setNotifications] = useState({
        emailNotifications: true,
        streamAlerts: true,
        weeklyDigest: false,
        marketingEmails: false
    });
    const [streamSettings, setStreamSettings] = useState({
        defaultQuality: '1080p',
        autoRecord: true,
        enableChat: true,
        chatModeration: true
    });

    const handleSaveProfile = () => {
        toast.success('Profile saved successfully');
    };

    const handleSaveNotifications = () => {
        toast.success('Notification settings saved');
    };

    const handleSaveStreamSettings = () => {
        toast.success('Stream settings saved');
    };

    return (
        <div className="space-y-6" data-testid="settings-page">
            {/* Header */}
            <div>
                <h1 className="text-2xl lg:text-3xl font-bold">Settings</h1>
                <p className="text-muted-foreground mt-1">
                    Manage your account and preferences
                </p>
            </div>

            <Tabs defaultValue="profile" className="space-y-6">
                <TabsList className="grid w-full grid-cols-2 lg:grid-cols-5 h-auto">
                    <TabsTrigger value="profile" className="gap-2 py-3" data-testid="tab-profile">
                        <User className="w-4 h-4" />
                        <span className="hidden sm:inline">Profile</span>
                    </TabsTrigger>
                    <TabsTrigger value="notifications" className="gap-2 py-3" data-testid="tab-notifications">
                        <Bell className="w-4 h-4" />
                        <span className="hidden sm:inline">Notifications</span>
                    </TabsTrigger>
                    <TabsTrigger value="streaming" className="gap-2 py-3" data-testid="tab-streaming">
                        <Video className="w-4 h-4" />
                        <span className="hidden sm:inline">Streaming</span>
                    </TabsTrigger>
                    <TabsTrigger value="appearance" className="gap-2 py-3" data-testid="tab-appearance">
                        {isDark ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                        <span className="hidden sm:inline">Appearance</span>
                    </TabsTrigger>
                    <TabsTrigger value="billing" className="gap-2 py-3" data-testid="tab-billing">
                        <CreditCard className="w-4 h-4" />
                        <span className="hidden sm:inline">Billing</span>
                    </TabsTrigger>
                </TabsList>

                {/* Profile Tab */}
                <TabsContent value="profile">
                    <Card>
                        <CardHeader>
                            <CardTitle>Profile Information</CardTitle>
                            <CardDescription>Update your personal details</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Avatar */}
                            <div className="flex items-center gap-4">
                                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white text-2xl font-bold">
                                    {profile.name.charAt(0)}
                                </div>
                                <div>
                                    <Button variant="outline" size="sm" className="gap-2">
                                        <Upload className="w-4 h-4" />
                                        Upload Photo
                                    </Button>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        JPG, PNG or GIF. Max 2MB.
                                    </p>
                                </div>
                            </div>

                            <Separator />

                            {/* Form */}
                            <div className="grid gap-4">
                                <div className="grid gap-2">
                                    <Label htmlFor="name">Full Name</Label>
                                    <Input
                                        id="name"
                                        value={profile.name}
                                        onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                                        data-testid="profile-name"
                                    />
                                </div>
                                <div className="grid gap-2">
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={profile.email}
                                        onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                                        data-testid="profile-email"
                                    />
                                </div>
                                <div className="grid gap-2">
                                    <Label htmlFor="company">Company</Label>
                                    <Input
                                        id="company"
                                        value={profile.company}
                                        onChange={(e) => setProfile({ ...profile, company: e.target.value })}
                                        data-testid="profile-company"
                                    />
                                </div>
                            </div>

                            <Button onClick={handleSaveProfile} className="gap-2" data-testid="save-profile-btn">
                                <Save className="w-4 h-4" />
                                Save Changes
                            </Button>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Notifications Tab */}
                <TabsContent value="notifications">
                    <Card>
                        <CardHeader>
                            <CardTitle>Notification Preferences</CardTitle>
                            <CardDescription>Choose what updates you receive</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <Label>Email Notifications</Label>
                                        <p className="text-sm text-muted-foreground">
                                            Receive updates about your streams via email
                                        </p>
                                    </div>
                                    <Switch
                                        checked={notifications.emailNotifications}
                                        onCheckedChange={(checked) =>
                                            setNotifications({ ...notifications, emailNotifications: checked })
                                        }
                                        data-testid="toggle-email-notifications"
                                    />
                                </div>

                                <Separator />

                                <div className="flex items-center justify-between">
                                    <div>
                                        <Label>Stream Alerts</Label>
                                        <p className="text-sm text-muted-foreground">
                                            Get notified when your stream starts or ends
                                        </p>
                                    </div>
                                    <Switch
                                        checked={notifications.streamAlerts}
                                        onCheckedChange={(checked) =>
                                            setNotifications({ ...notifications, streamAlerts: checked })
                                        }
                                        data-testid="toggle-stream-alerts"
                                    />
                                </div>

                                <Separator />

                                <div className="flex items-center justify-between">
                                    <div>
                                        <Label>Weekly Digest</Label>
                                        <p className="text-sm text-muted-foreground">
                                            Get a weekly summary of your streaming analytics
                                        </p>
                                    </div>
                                    <Switch
                                        checked={notifications.weeklyDigest}
                                        onCheckedChange={(checked) =>
                                            setNotifications({ ...notifications, weeklyDigest: checked })
                                        }
                                        data-testid="toggle-weekly-digest"
                                    />
                                </div>

                                <Separator />

                                <div className="flex items-center justify-between">
                                    <div>
                                        <Label>Marketing Emails</Label>
                                        <p className="text-sm text-muted-foreground">
                                            Receive tips, product updates, and offers
                                        </p>
                                    </div>
                                    <Switch
                                        checked={notifications.marketingEmails}
                                        onCheckedChange={(checked) =>
                                            setNotifications({ ...notifications, marketingEmails: checked })
                                        }
                                        data-testid="toggle-marketing"
                                    />
                                </div>
                            </div>

                            <Button onClick={handleSaveNotifications} className="gap-2" data-testid="save-notifications-btn">
                                <Save className="w-4 h-4" />
                                Save Preferences
                            </Button>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Streaming Tab */}
                <TabsContent value="streaming">
                    <Card>
                        <CardHeader>
                            <CardTitle>Stream Settings</CardTitle>
                            <CardDescription>Configure your default streaming preferences</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <Label>Auto Record</Label>
                                        <p className="text-sm text-muted-foreground">
                                            Automatically record all your streams
                                        </p>
                                    </div>
                                    <Switch
                                        checked={streamSettings.autoRecord}
                                        onCheckedChange={(checked) =>
                                            setStreamSettings({ ...streamSettings, autoRecord: checked })
                                        }
                                        data-testid="toggle-auto-record"
                                    />
                                </div>

                                <Separator />

                                <div className="flex items-center justify-between">
                                    <div>
                                        <Label>Enable Chat</Label>
                                        <p className="text-sm text-muted-foreground">
                                            Allow viewers to chat during streams
                                        </p>
                                    </div>
                                    <Switch
                                        checked={streamSettings.enableChat}
                                        onCheckedChange={(checked) =>
                                            setStreamSettings({ ...streamSettings, enableChat: checked })
                                        }
                                        data-testid="toggle-enable-chat"
                                    />
                                </div>

                                <Separator />

                                <div className="flex items-center justify-between">
                                    <div>
                                        <Label>Chat Moderation</Label>
                                        <p className="text-sm text-muted-foreground">
                                            Automatically filter inappropriate messages
                                        </p>
                                    </div>
                                    <Switch
                                        checked={streamSettings.chatModeration}
                                        onCheckedChange={(checked) =>
                                            setStreamSettings({ ...streamSettings, chatModeration: checked })
                                        }
                                        data-testid="toggle-moderation"
                                    />
                                </div>
                            </div>

                            <Button onClick={handleSaveStreamSettings} className="gap-2" data-testid="save-stream-settings-btn">
                                <Save className="w-4 h-4" />
                                Save Settings
                            </Button>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Appearance Tab */}
                <TabsContent value="appearance">
                    <Card>
                        <CardHeader>
                            <CardTitle>Appearance</CardTitle>
                            <CardDescription>Customize how StreamHub looks</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <Label>Dark Mode</Label>
                                    <p className="text-sm text-muted-foreground">
                                        Switch between light and dark themes
                                    </p>
                                </div>
                                <Switch
                                    checked={isDark}
                                    onCheckedChange={toggleTheme}
                                    data-testid="toggle-dark-mode"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4 pt-4">
                                <div
                                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                                        !isDark ? 'border-primary bg-primary/5' : 'border-border'
                                    }`}
                                    onClick={() => !isDark || toggleTheme()}
                                >
                                    <div className="w-full h-20 bg-white rounded-lg border mb-3" />
                                    <p className="text-sm font-medium text-center">Light</p>
                                </div>
                                <div
                                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                                        isDark ? 'border-primary bg-primary/5' : 'border-border'
                                    }`}
                                    onClick={() => isDark || toggleTheme()}
                                >
                                    <div className="w-full h-20 bg-gray-900 rounded-lg border border-gray-700 mb-3" />
                                    <p className="text-sm font-medium text-center">Dark</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Billing Tab */}
                <TabsContent value="billing">
                    <Card>
                        <CardHeader>
                            <CardTitle>Billing & Subscription</CardTitle>
                            <CardDescription>Manage your subscription and payment methods</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="p-4 rounded-xl bg-gradient-to-r from-primary/10 to-purple-500/10 border">
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Current Plan</p>
                                        <p className="text-xl font-bold">Free Plan</p>
                                    </div>
                                    <Button>Upgrade to Pro</Button>
                                </div>
                                <div className="grid grid-cols-3 gap-4 text-center pt-4 border-t">
                                    <div>
                                        <p className="text-2xl font-bold">3</p>
                                        <p className="text-xs text-muted-foreground">Streams/month</p>
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">720p</p>
                                        <p className="text-xs text-muted-foreground">Max quality</p>
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">2</p>
                                        <p className="text-xs text-muted-foreground">Destinations</p>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <h4 className="font-medium">Pro Plan Benefits</h4>
                                <ul className="space-y-2 text-sm text-muted-foreground">
                                    <li className="flex items-center gap-2">
                                        <Shield className="w-4 h-4 text-primary" />
                                        Unlimited streams
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Shield className="w-4 h-4 text-primary" />
                                        4K streaming quality
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Shield className="w-4 h-4 text-primary" />
                                        Unlimited destinations
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Shield className="w-4 h-4 text-primary" />
                                        Custom branding & overlays
                                    </li>
                                </ul>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default Settings;
